import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare, ToggleLeft, ToggleRight, Save, Info,
  ChevronDown, ChevronUp, Bell, Navigation, CheckCircle2,
} from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

// ── Default templates (mirrors backend DEFAULT_TEMPLATES) ─────────────────────

const DEFAULTS = {
  dayBefore: {
    enabled: true,
    body: 'Hi {{firstName}}, just a heads up — your {{companyName}} service is scheduled for tomorrow ({{serviceDate}}) around {{serviceTime}}. {{techName}} will be your technician.',
  },
  onTheWay: {
    enabled: true,
    body: 'Hi {{firstName}}, {{techName}} is on the way and should arrive in approximately {{eta}}. Thank you for choosing {{companyName}}!',
  },
  completion: {
    enabled: true,
    body: 'Hi {{firstName}}, your {{companyName}} service is complete — thank you for choosing us! {{paymentLink}}',
  },
};

const TEMPLATE_META = [
  {
    key: 'dayBefore',
    title: 'Day-Before Reminder',
    description: 'Sent when operator clicks "Send Tomorrow\'s Notifications" from Dispatch.',
    icon: Bell,
    color: 'indigo',
    variables: ['firstName', 'companyName', 'techName', 'serviceDate', 'serviceTime', 'jobAddress'],
  },
  {
    key: 'onTheWay',
    title: 'On the Way',
    description: 'Triggered automatically when a technician clicks "En Route" in Dispatch.',
    icon: Navigation,
    color: 'amber',
    variables: ['firstName', 'companyName', 'techName', 'eta'],
  },
  {
    key: 'completion',
    title: 'Completion Message',
    description: 'Sent when a job is marked complete. Include {{paymentLink}} for the payment URL.',
    icon: CheckCircle2,
    color: 'emerald',
    variables: ['firstName', 'companyName', 'techName', 'serviceDate', 'paymentLink'],
  },
];

const COLOR_MAP = {
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'text-indigo-600', chip: 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100', toggle: 'bg-indigo-600' },
  amber:  { bg: 'bg-amber-50',  border: 'border-amber-200',  icon: 'text-amber-600',  chip: 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100',   toggle: 'bg-amber-500'  },
  emerald:{ bg: 'bg-emerald-50',border: 'border-emerald-200',icon: 'text-emerald-600',chip: 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100',toggle: 'bg-emerald-600'},
};

// ── Variable chip click handler ────────────────────────────────────────────────

function insertAtCursor(textareaRef, variable, body, setBody) {
  const el = textareaRef.current;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const token = `{{${variable}}}`;
  const next = body.substring(0, start) + token + body.substring(end);
  setBody(next);
  setTimeout(() => {
    el.focus();
    el.selectionStart = start + token.length;
    el.selectionEnd   = start + token.length;
  }, 0);
}

// ── Template Card ──────────────────────────────────────────────────────────────

function TemplateCard({ meta, saved, onSave }) {
  const c = COLOR_MAP[meta.color];
  const Icon = meta.icon;

  const [enabled, setEnabled] = useState(saved?.enabled ?? DEFAULTS[meta.key].enabled);
  const [body, setBody]       = useState(saved?.body ?? DEFAULTS[meta.key].body);
  const [saving, setSaving]   = useState(false);
  const [open, setOpen]       = useState(true);
  const textareaRef = useRef(null);

  // Sync from parent when saved changes (after initial load)
  useEffect(() => {
    if (saved) {
      setEnabled(saved.enabled ?? true);
      setBody(saved.body ?? DEFAULTS[meta.key].body);
    }
  }, [saved, meta.key]);

  const charCount = body.length;
  const smsSegments = Math.ceil(charCount / 160) || 1;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(meta.key, { enabled, body });
      toast.success(`${meta.title} saved`);
    } catch (err) {
      toast.error('Save failed — please try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded-xl border ${c.border} bg-white shadow-sm overflow-hidden`}
      data-testid={`visit-msg-card-${meta.key}`}
    >
      {/* Card header */}
      <div
        className={`flex items-center justify-between gap-2 px-4 sm:px-5 py-4 cursor-pointer ${c.bg}`}
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-1.5 rounded-md ${c.bg} shrink-0`}>
            <Icon className={`h-4 w-4 ${c.icon}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{meta.title}</p>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{meta.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Enabled toggle */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setEnabled((v) => !v); }}
            className="flex items-center gap-1.5 text-xs font-medium"
            data-testid={`toggle-${meta.key}`}
          >
            {enabled ? (
              <ToggleRight className={`h-5 w-5 ${c.icon}`} />
            ) : (
              <ToggleLeft className="h-5 w-5 text-gray-300" />
            )}
            <span className={enabled ? c.icon : 'text-gray-400'}>
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
          </button>
          {open
            ? <ChevronUp className="h-4 w-4 text-gray-400" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />
          }
        </div>
      </div>

      {/* Card body */}
      {open && (
        <div className="px-4 sm:px-5 py-4 space-y-4">
          {/* Variable chips — horizontally scrollable on narrow widths */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1.5">
              Available variables — click to insert at cursor
            </p>
            <div className="flex flex-wrap gap-1.5 max-w-full">
              {meta.variables.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertAtCursor(textareaRef, v, body, setBody)}
                  className={`text-[11px] font-mono px-2 py-0.5 rounded border transition-colors ${c.chip}`}
                  data-testid={`var-chip-${meta.key}-${v}`}
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>

          {/* Textarea */}
          <div>
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              disabled={!enabled}
              className={`
                w-full text-sm rounded-lg border px-3 py-2.5 font-mono leading-relaxed resize-none
                focus:outline-none focus:ring-2 focus:ring-indigo-300
                ${enabled ? 'border-gray-200 bg-white text-gray-900' : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'}
              `}
              placeholder="Enter message template…"
              data-testid={`template-body-${meta.key}`}
            />
            {/* Char count */}
            <div className="flex justify-between mt-1">
              <span className={`text-xs ${charCount > 320 ? 'text-red-500' : 'text-gray-400'}`}>
                {charCount} chars
              </span>
              <span className="text-xs text-gray-400">
                {smsSegments === 1 ? '1 SMS segment' : `${smsSegments} SMS segments`}
              </span>
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !body.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
              data-testid={`save-btn-${meta.key}`}
            >
              <Save className="h-3.5 w-3.5" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function VisitMessaging() {
  const { profile } = useAuth();
  const companyId = profile?.companyId;

  const [templates, setTemplates] = useState(null); // null = loading
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!companyId) return;
    const load = async () => {
      try {
        const snap = await getDoc(
          doc(db, 'companies', companyId, 'settings', 'visitMessaging')
        );
        setTemplates(snap.exists() ? snap.data() : {});
      } catch (err) {
        console.error('[VisitMessaging] load error:', err);
        setTemplates({});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [companyId]);

  const handleSave = useCallback(async (key, value) => {
    if (!companyId) throw new Error('No company');
    const ref = doc(db, 'companies', companyId, 'settings', 'visitMessaging');
    await setDoc(ref, { [key]: value }, { merge: true });
    setTemplates((prev) => ({ ...(prev || {}), [key]: value }));
  }, [companyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-5 w-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="visit-messaging-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="h-5 w-5 text-gray-700" />
          <h2 className="text-lg font-bold text-gray-900">Visit Messaging</h2>
        </div>
        <p className="text-sm text-gray-500">
          Customize the SMS messages sent to customers at each visit milestone.
          Use variables like <code className="bg-gray-100 rounded px-1 text-xs">{'{{firstName}}'}</code> for personalization.
        </p>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
        <Info className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">
          Messages are sent via your company's provisioned SMS number. Disabled message types are silently
          skipped — no SMS is sent. Templates are saved per company.
        </p>
      </div>

      {/* Template cards */}
      {TEMPLATE_META.map((meta) => (
        <TemplateCard
          key={meta.key}
          meta={meta}
          saved={templates?.[meta.key] ?? null}
          onSave={handleSave}
        />
      ))}
    </div>
  );
}
